import React, { FC, useEffect, useId, useRef } from "react";
import Avatar from "shared/Avatar/Avatar";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NcImage from "shared/NcImage/NcImage";
import LikeSaveBtns from "./LikeSaveBtns";
import TabDetail from "./TabDetail";
import Glide from "@glidejs/glide";
import SectionLargeSlider from "containers/PageHome/SectionLargeSlider";
import { useQuery } from "@apollo/client";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { GET_REQ_SONG } from "DataProvider/Queries_gql";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import AppStorePng from "../../images/socials/appstore.png";
import PlayStorePng from "../../images/socials/playstore.png";
import I404Png from "images/404.png";

import ReactGA4 from "react-ga4";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";

export interface SongPageProps {
  className?: string;
  isPreviewMode?: boolean;
  onClickNext?: () => void;
  onClickPrev?: () => void;
}
const SongPage: FC<SongPageProps> = ({
  className = "",
  isPreviewMode,
  onClickNext = () => {},
  onClickPrev = () => {},
}) => {
  const params: any = useParams();

  const Project_Id: any = params.PROJECT_ID;

  const sliderRef = useRef(null);
  const id = useId();
  const UNIQUE_CLASS = "glidejs" + id.replace(/:/g, "_");

  const { loading, error, data } = useQuery(GET_REQ_SONG, {
    variables: {
      filter: { id: Project_Id },
    },
  });

  useEffect(() => {
    ReactGA4.send({ hitType: "pageview", page: window.location.pathname });
    if (!sliderRef.current) {
      return;
    }
    const OPTIONS: Glide.Options = {
      perView: 1,
      gap: 32,
      bound: true,
      breakpoints: {
        1280: {
          gap: 28,
          perView: 1,
        },
        1024: {
          gap: 20,
          perView: 1,
        },
        768: {
          gap: 20,
          perView: 1,
        },
        500: {
          gap: 20,
          perView: 1,
        },
      },
    };
    let slider = new Glide(`.${UNIQUE_CLASS}`, OPTIONS);
    slider.mount();
    // @ts-ignore
    return () => slider.destroy();
  }, [sliderRef, UNIQUE_CLASS, data]);
  if (loading) {
    return (
      <div className="container mt-40 mb-40 flex flex-col mx-auto justify-center items-center">
        <svg
          className={`motion-safe:animate-spin dark:text-white text-black -ml-1 mr-3 h-10 w-10`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <div className="font-thin text-sm mt-4">Loading Song</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-40 mb-40 flex flex-col mx-auto justify-center items-center">
        <ExclamationTriangleIcon className="text-red-600 h-10 w-10" />
        <div className="font-thin text-sm mt-4">{error.message}</div>
      </div>
    );
  }
  return (
    <div className={`nc-SectionSliderCollections mb-28 ${className}`}>
      <BgGlassmorphism />
      {data?.ProjectPlayer ? (
        <div className={`${UNIQUE_CLASS} flow-root`} ref={sliderRef}>
          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides">
              <li className={`glide__slide `}>
                <main className="container mt-11 flex ">
                  <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
                    {/* CONTENT */}
                    <div className="space-y-8 lg:space-y-10">
                      {/* HEADING */}
                      <div className="relative">
                        <SectionLargeSlider
                          nameP={data?.ProjectPlayer?.name}
                          headerImage={data?.ProjectPlayer?.headerImage}
                          mixdownVideo={data?.ProjectPlayer?.mixdownVideo}
                          ownerAvatar={data?.ProjectPlayer?.owner.avatarUrl}
                          ownerName={data?.ProjectPlayer?.owner.artistName}
                          ownerFCMTOKEN={data?.ProjectPlayer?.owner.fcm_token}
                          description={data?.ProjectPlayer?.description}
                          commentsCount={data?.ProjectPlayer?.commentsCount}
                          ClapsCount={data?.ProjectPlayer?.clapsCount}
                          TracksCount={data?.ProjectPlayer?.tracksCount}
                          ShareLink={data?.ProjectPlayer?.mixdownVideo}
                          ProjectID={data?.ProjectPlayer?.id}
                          ProjectTracksArray={data?.ProjectPlayer?.tracks}
                          ShowAvatarRails={false}
                        />
                      </div>
                    </div>

                    {/* SIDEBAR */}
                    <div className="pt-10 lg:pt-0 border-t-2 border-neutral-200 dark:border-neutral-700 lg:border-t-0 flex flex-col justify-center items-center">
                      <RenderSection1
                        nameP={data?.ProjectPlayer?.name}
                        headerImage={data?.ProjectPlayer?.headerImage}
                        mixdownVideo={data?.ProjectPlayer?.mixdownVideo}
                        ownerAvatar={data?.ProjectPlayer?.owner.avatarUrl}
                        ownerId={data?.ProjectPlayer?.owner.id}
                        ownerName={data?.ProjectPlayer?.owner.artistName}
                        ownerFCMTOKEN={data?.ProjectPlayer?.owner.fcm_token}
                        description={data?.ProjectPlayer?.description}
                        commentsCount={data?.ProjectPlayer?.commentsCount}
                        TracksCount={data?.ProjectPlayer?.tracksCount}
                        ShareLink={data?.ProjectPlayer?.mixdownVideo}
                        ProjectID={data?.ProjectPlayer?.id}
                        ProjectTracksArray={data?.ProjectPlayer?.tracks}
                      />
                    </div>
                  </div>
                </main>
              </li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="nc-Page404">
          <Helmet>
            <title>404 || Djaminn</title>
          </Helmet>
          <div className="container relative pt-5 pb-16 lg:pb-20 lg:pt-5">
            {/* HEADER */}
            <header className="text-center max-w-2xl mx-auto space-y-2">
              <NcImage src={I404Png} />
              <span className="block text-sm text-neutral-800 sm:text-base dark:text-neutral-200 tracking-wider font-medium">
                THE SONG YOU WERE LOOKING FOR DOESN'T EXIST.{" "}
              </span>
              <div className="pt-8">
                <ButtonPrimary href="/">Return Home Page</ButtonPrimary>
              </div>
            </header>
          </div>
        </div>
      )}
    </div>
  );
};
export default SongPage;

const RenderSection1 = (props: any) => {
  return (
    <div className="divide-y w-full divide-neutral-100 dark:divide-neutral-800">
      <Helmet>
        <title>{`${props.nameP}-Djaminn`}</title>
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={props.nameP} />
        <meta property="og:description" content={props.description} />
        <meta property="og:image" content={props.headerImage} />
      </Helmet>
      {/* ---------- 1 ----------  */}
      <div className="pb-9 space-y-5">
        <div className="flex justify-end items-center">
          <LikeSaveBtns
            ShareLink={`${window.location.origin}/song/${props.ProjectID}`}
          />
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-Montserrat italic">
          {props.nameP}
        </h2>

        {/* ---------- 4 ----------  */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-8 text-sm">
          <div className="flex items-center ">
            <Link
              to={{
                pathname: `/view-profile/${props.ownerId}`,
                state: {
                  id: props.ownerId,
                },
              }}
            >
              <Avatar
                imgUrl={props.ownerAvatar}
                sizeClass="h-9 w-9"
                radius="rounded-full"
              />
            </Link>
            <span className="ml-2.5 text-neutral-500 dark:text-neutral-400 flex flex-col">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium flex items-center">
                <Link
                  to={{
                    pathname: `/view-profile/${props.ownerId}`,
                    state: {
                      id: props.ownerId,
                    },
                  }}
                >
                  <span>{props.ownerName}</span>
                </Link>
              </span>
              <span className="text-sm">Creator</span>
            </span>
          </div>
          <div className="hidden sm:block h-6 border-l border-neutral-200 dark:border-neutral-700"></div>
        </div>
      </div>

      {/* ---------- 7 ----------  */}
      {/* PRICE */}
      <div className="pb-9 pt-14">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
          <div className="flex-1 flex flex-col sm:flex-row items-baseline p-6 border-2 border-green-500 rounded-xl relative">
            <span className="absolute bottom-full translate-y-1 py-1 px-1.5 bg-white dark:bg-neutral-900 text-sm text-neutral-500 dark:text-neutral-400">
              Description
            </span>

            <span className="text-lg text-neutral-400 sm:ml-5">
              {props.description ? props.description : "No Description Found"}
            </span>
          </div>
        </div>
      </div>

      {/* ---------- 9 ----------  */}
      <div className="pt-9 pb-9">
        {localStorage.getItem("token") ? (
          <TabDetail
            ownerFCMTOKEN={props.ownerFCMTOKEN}
            ProjectTracksArray={props.ProjectTracksArray}
            ProjectID={props.ProjectID}
          />
        ) : (
          <>
            <div className="container flex flex-row items-center justify-center mt-4 border rounded-xl shadow dark:border-slate-800 border-slate-400 py-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="#fff"
                className="w-6 h-6"
              >
                <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
              </svg>

              <div className="mx-6">
                <p className="text-center text-slate-600">
                  Login to See Comments
                </p>
                <Link to={"/login"}>
                  <p className="text-center text-sm hover:text-pink-600">
                    Login Here.
                  </p>
                </Link>
              </div>
            </div>
            <div className="flex flex-row w-full justify-center items-center">
              <div className="w-min h-16 flex items-center mt-3">
                <a
                  className="h-auto w-36"
                  href="https://apps.apple.com/tt/app/djaminn-create-songs-together/id1634589883"
                >
                  <img
                    src={AppStorePng}
                    alt="App Store"
                    className="h-auto w-36 object-contain "
                  />
                </a>
              </div>
              <div className="w-min h-16 flex items-center mt-3 ml-3">
                <a
                  className="h-auto w-36"
                  href="https://play.google.com/store/apps/details?id=com.djamin"
                >
                  <img
                    src={PlayStorePng}
                    alt="Play Sotre"
                    className="h-auto w-36 object-contain "
                  />
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
